import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
    Archive, Play, StopCircle, Printer, RefreshCw,
    Banknote, CreditCard, Smartphone, ArrowRightLeft,
    CheckCircle2, AlertTriangle, Clock, Users,
} from 'lucide-react';

const API = `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/v1/inventario`;

const FORMA_META = {
    efectivo:      { label: 'Efectivo',       icon: <Banknote size={16} />,       color: 'marca' },
    transferencia: { label: 'Transferencia',  icon: <ArrowRightLeft size={16} />, color: 'marca' },
    tarjeta:       { label: 'Tarjeta',        icon: <CreditCard size={16} />,     color: 'marca' },
    qr:            { label: 'QR',             icon: <Smartphone size={16} />,     color: 'marca' },
};

const COLOR = {
    // Un indicador comun. El color no distingue una metrica de otra: para eso
    // estan su titulo y su icono.
    marca:    'bg-brand-50 border-brand-200 text-brand-800 dark:bg-brand-500/10 dark:border-brand-500/20 dark:text-brand-400',
    // Informa algo que anda mal. Aca el rojo si esta diciendo algo.
    problema: 'bg-error-50 border-error-200 text-error-700 dark:bg-error-500/10 dark:border-error-500/20 dark:text-error-400',
    // Dato de contexto, sin peso propio.
    neutro:   'bg-slate-50 border-slate-200 text-slate-700 dark:bg-slate-800/60 dark:border-white/10 dark:text-slate-300',
};

const fmt = (n) => Number(n || 0).toLocaleString('es-PY');
const fmtTime = (d) => new Date(d).toLocaleString('es-PY');

export default function CierreCaja() {
    const currentUser = JSON.parse(sessionStorage.getItem('user') || '{}');
    const userRol = currentUser.rol || (currentUser.is_admin ? 'Administrador' : 'Sin rol');
    const esAdmin = userRol === 'Administrador' || userRol === 'Supervisor';

    return esAdmin ? <VistaAdmin /> : <VistaCajero />;
}

/* ─────────────────────────────────────────────────────────
   VISTA ADMIN / SUPERVISOR — lista de turnos abiertos
───────────────────────────────────────────────────────── */
function VistaAdmin() {
    const [turnos, setTurnos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [reporte, setReporte] = useState(null);
    const [cerrando, setCerrando] = useState(null); // id del turno que se está cerrando

    // Formulario de cierre por turno
    const [montoDeclarado, setMontoDeclarado] = useState('');
    const [observaciones, setObservaciones] = useState('');
    const [expandedId, setExpandedId] = useState(null);

    const fetchActivos = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/caja/activos/`);
            setTurnos(data);
        } catch { }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { fetchActivos(); }, [fetchActivos]);

    const handleCerrar = async (e, cajaId) => {
        e.preventDefault();
        if (!window.confirm('¿Confirmar cierre del turno de este cajero?')) return;
        setCerrando(cajaId);
        try {
            const { data } = await axios.post(
                `${API}/caja/${cajaId}/forzar_cierre/`,
                {
                    monto_efectivo_declarado: montoDeclarado !== '' ? montoDeclarado : null,
                    observaciones,
                });
            setReporte(data);
        } catch (err) {
            alert(err.response?.data?.error || 'Error al cerrar el turno.');
        } finally { setCerrando(null); }
    };

    const handleImprimir = () => window.print();

    if (loading) return (
        <div className="cargando">
            <RefreshCw className="animate-spin text-brand-800" size={32} />
        </div>
    );

    if (reporte) return (
        <div className="max-w-2xl mx-auto space-y-6 pb-10">
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="titulo-pagina">Reporte de Cierre</h2>
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                        Turno cerrado · {fmtTime(reporte.fecha_cierre)}
                    </p>
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={handleImprimir}
                        className="flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-100 dark:bg-white/10 hover:bg-slate-200 dark:hover:bg-white/20 text-slate-700 dark:text-white border border-slate-200 dark:border-white/10 text-sm font-semibold transition-all print:hidden"
                    >
                        <Printer size={16} /> Imprimir
                    </button>
                    <button
                        onClick={() => { setReporte(null); setMontoDeclarado(''); setObservaciones(''); setExpandedId(null); fetchActivos(); }}
                        className="flex items-center gap-2 px-4 py-2 rounded-xl bg-brand-600 hover:bg-brand-500 text-negro-950 text-sm font-semibold transition-all print:hidden"
                    >
                        <RefreshCw size={14} /> Volver
                    </button>
                </div>
            </div>
            <Reporte data={reporte} />
        </div>
    );

    return (
        <div className="max-w-3xl mx-auto space-y-6 pb-10">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                        <Users size={22} className="text-brand-800" /> Turnos Activos
                    </h2>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
                        {turnos.length === 0
                            ? 'No hay turnos abiertos en este momento.'
                            : `${turnos.length} turno${turnos.length !== 1 ? 's' : ''} abierto${turnos.length !== 1 ? 's' : ''}`}
                    </p>
                </div>
                <button
                    onClick={fetchActivos}
                    className="p-2.5 rounded-xl border border-slate-200 dark:border-white/10 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-white/10 transition-all"
                >
                    <RefreshCw size={16} />
                </button>
            </div>

            {turnos.length === 0 && (
                <div className="estado-vacio">
                    <Archive size={48} className="mx-auto mb-3 opacity-30" />
                    <p className="font-semibold">Sin turnos abiertos</p>
                    <p className="text-sm mt-1">Cuando un cajero abra su turno aparecerá aquí.</p>
                </div>
            )}

            {turnos.map(turno => {
                const isOpen = expandedId === turno.id;
                const duracion = Math.floor((Date.now() - new Date(turno.fecha_apertura)) / 60000);
                return (
                    <div key={turno.id} className="bg-white dark:bg-slate-900/60 border border-slate-200 dark:border-white/10 rounded-2xl shadow-sm overflow-hidden">
                        {/* Cabecera del turno */}
                        <div className="p-5 flex items-center justify-between gap-4">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-brand-50 dark:bg-brand-500/10 border border-brand-200 dark:border-brand-500/20 flex items-center justify-center text-brand-800 dark:text-brand-400 font-bold text-base shrink-0">
                                    {turno.cajero_nombre.charAt(0).toUpperCase()}
                                </div>
                                <div>
                                    <p className="font-bold text-slate-900 dark:text-white">{turno.cajero_nombre}</p>
                                    <p className="text-xs text-slate-400">{turno.cajero_email}</p>
                                </div>
                            </div>
                            <div className="text-right">
                                <p className="text-xs text-slate-400 flex items-center justify-end gap-1">
                                    <Clock size={11} /> {duracion} min
                                </p>
                                <p className="font-bold text-slate-900 dark:text-white">Gs. {fmt(turno.total_ventas)}</p>
                                <p className="text-xs text-slate-400">{turno.cantidad_ventas} venta{turno.cantidad_ventas !== 1 ? 's' : ''}</p>
                            </div>
                        </div>

                        {/* KPIs del turno */}
                        <div className="px-5 pb-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
                            <div className="bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-white/10 rounded-xl p-3 text-center">
                                <p className="text-[10px] font-bold uppercase text-slate-400 tracking-wide">Efectivo inicial</p>
                                <p className="text-sm font-bold text-slate-700 dark:text-slate-200 mt-0.5">Gs. {fmt(turno.monto_apertura)}</p>
                            </div>
                            <div className="bg-exito-50 dark:bg-exito-500/10 border border-exito-200 dark:border-exito-500/20 rounded-xl p-3 text-center">
                                <p className="text-[10px] font-bold uppercase text-exito-600 dark:text-exito-400 tracking-wide">Total ventas</p>
                                <p className="text-sm font-bold text-exito-700 dark:text-exito-300 mt-0.5">Gs. {fmt(turno.total_ventas)}</p>
                            </div>
                            <div className="bg-brand-50 dark:bg-brand-500/10 border border-brand-200 dark:border-brand-500/20 rounded-xl p-3 text-center">
                                <p className="text-[10px] font-bold uppercase text-brand-800 dark:text-brand-400 tracking-wide">Efectivo esperado</p>
                                <p className="text-sm font-bold text-brand-800 dark:text-brand-300 mt-0.5">Gs. {fmt(turno.efectivo_esperado)}</p>
                            </div>
                        </div>

                        {/* Desglose forma de pago */}
                        <div className="px-5 pb-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2">
                            {Object.entries(FORMA_META).map(([key, meta]) => {
                                const d = turno.desglose_pago?.[key] || { total: 0, cantidad: 0 };
                                return (
                                    <div key={key} className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-xs ${COLOR[meta.color]}`}>
                                        <span className="opacity-70">{meta.icon}</span>
                                        <div>
                                            <p className="font-bold uppercase tracking-wide opacity-70">{meta.label}</p>
                                            <p className="font-bold">Gs. {fmt(d.total)}</p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Botón cerrar */}
                        <div className="px-5 pb-5">
                            <button
                                onClick={() => {
                                    setExpandedId(isOpen ? null : turno.id);
                                    setMontoDeclarado('');
                                    setObservaciones('');
                                }}
                                className="w-full py-2.5 rounded-xl border border-error-200 dark:border-error-500/30 text-error-500 dark:text-error-400 hover:bg-error-50 dark:hover:bg-error-500/10 text-sm font-semibold transition-all flex items-center justify-center gap-2"
                            >
                                <StopCircle size={15} />
                                {isOpen ? 'Cancelar' : 'Cerrar este turno'}
                            </button>
                        </div>

                        {/* Formulario de cierre expandible */}
                        {isOpen && (
                            <form
                                onSubmit={e => handleCerrar(e, turno.id)}
                                className="border-t border-error-100 dark:border-error-500/20 bg-error-50/30 dark:bg-error-500/5 px-5 py-5 space-y-4"
                            >
                                <p className="text-xs font-bold text-error-500 dark:text-error-400 uppercase tracking-wider">
                                    Cierre de turno — {turno.cajero_nombre}
                                </p>
                                <div>
                                    <label htmlFor="caja-efectivo-contado-al-cierre-gs" className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-1.5">
                                        Efectivo contado al cierre (Gs.)
                                    </label>
                                    <input id="caja-efectivo-contado-al-cierre-gs"
                                        type="number" min="0" step="any"
                                        value={montoDeclarado}
                                        onChange={e => setMontoDeclarado(e.target.value)}
                                        placeholder={`Esperado: Gs. ${fmt(turno.efectivo_esperado)}`}
                                        className="w-full px-4 py-3 rounded-xl bg-white dark:bg-white/5 border border-slate-200 dark:border-white/10 text-slate-900 dark:text-white text-lg font-bold focus:outline-none focus:ring-2 focus:ring-error-500/40"
                                    />
                                </div>
                                <div>
                                    <label htmlFor="caja-observaciones" className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-1.5">
                                        Observaciones
                                    </label>
                                    <textarea id="caja-observaciones"
                                        rows={2}
                                        value={observaciones}
                                        onChange={e => setObservaciones(e.target.value)}
                                        placeholder="Novedades, diferencias, etc."
                                        className="w-full px-4 py-3 rounded-xl bg-white dark:bg-white/5 border border-slate-200 dark:border-white/10 text-slate-900 dark:text-white text-sm resize-none focus:outline-none focus:ring-2 focus:ring-error-500/40"
                                    />
                                </div>
                                <button
                                    type="submit"
                                    disabled={cerrando === turno.id}
                                    className="w-full py-3 rounded-xl bg-error-500 hover:bg-error-600 disabled:opacity-50 text-white font-bold text-sm transition-all flex items-center justify-center gap-2"
                                >
                                    <StopCircle size={16} />
                                    {cerrando === turno.id ? 'Cerrando...' : 'Confirmar Cierre'}
                                </button>
                            </form>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

/* ─────────────────────────────────────────────────────────
   VISTA CAJERO — su propio turno
───────────────────────────────────────────────────────── */
function VistaCajero() {
    const [turno, setTurno] = useState(null);
    const [loading, setLoading] = useState(true);
    const [reporte, setReporte] = useState(null);
    const [montoApertura, setMontoApertura] = useState('');
    const [montoDeclarado, setMontoDeclarado] = useState('');
    const [observaciones, setObservaciones] = useState('');
    const [cerrando, setCerrando] = useState(false);

    const fetchEstado = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/caja/estado/`);
            setTurno(data.estado === 'abierto' ? data.caja : null);
        } catch { }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { fetchEstado(); }, [fetchEstado]);

    const handleAbrir = async (e) => {
        e.preventDefault();
        try {
            await axios.post(`${API}/caja/abrir/`, { monto_apertura: montoApertura || 0 });
            setMontoApertura('');
            fetchEstado();
        } catch (err) {
            alert(err.response?.data?.error || 'Error al abrir turno.');
        }
    };

    const handleCerrar = async (e) => {
        e.preventDefault();
        if (!window.confirm('¿Confirmar cierre de caja?')) return;
        setCerrando(true);
        try {
            const { data } = await axios.post(`${API}/caja/cerrar/`, {
                monto_efectivo_declarado: montoDeclarado !== '' ? montoDeclarado : null,
                observaciones,
            });
            setReporte(data);
            setTurno(null);
        } catch (err) {
            alert(err.response?.data?.error || 'Error al cerrar caja.');
        } finally { setCerrando(false); }
    };

    if (loading) return (
        <div className="cargando">
            <RefreshCw className="animate-spin text-brand-800" size={32} />
        </div>
    );

    if (reporte) return (
        <div className="max-w-2xl mx-auto space-y-6 pb-10">
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="titulo-pagina">Reporte de Cierre</h2>
                    <p className="text-sm text-slate-500 dark:text-slate-400">Turno cerrado · {fmtTime(reporte.fecha_cierre)}</p>
                </div>
                <div className="flex gap-2">
                    <button onClick={() => window.print()}
                        className="flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-100 dark:bg-white/10 hover:bg-slate-200 dark:hover:bg-white/20 text-slate-700 dark:text-white border border-slate-200 dark:border-white/10 text-sm font-semibold transition-all print:hidden">
                        <Printer size={16} /> Imprimir
                    </button>
                    <button onClick={() => { setReporte(null); fetchEstado(); }}
                        className="flex items-center gap-2 px-4 py-2 rounded-xl bg-brand-600 hover:bg-brand-500 text-negro-950 text-sm font-semibold transition-all print:hidden">
                        <Play size={14} /> Nuevo Turno
                    </button>
                </div>
            </div>
            <Reporte data={reporte} />
        </div>
    );

    if (!turno) return (
        <div className="max-w-md mx-auto space-y-6 pt-4">
            <div className="text-center mb-2">
                <Archive size={48} className="mx-auto text-slate-300 dark:text-slate-600 mb-3" />
                <h2 className="titulo-pagina">Sin Turno Activo</h2>
                <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">Abrí tu turno para comenzar a registrar ventas.</p>
            </div>
            <form onSubmit={handleAbrir} className="bg-white dark:bg-slate-900/60 border border-slate-200 dark:border-white/10 rounded-2xl p-6 space-y-5 shadow-lg">
                <h3 className="font-bold text-slate-900 dark:text-white flex items-center gap-2">
                    <Play size={18} className="text-brand-800" /> Apertura de Turno
                </h3>
                <div>
                    <label htmlFor="caja-efectivo-inicial-en-caja-gs" className="etiqueta-campo">Efectivo inicial en caja (Gs.)</label>
                    <input id="caja-efectivo-inicial-en-caja-gs"
                        type="number" min="0" step="any"
                        value={montoApertura}
                        onChange={e => setMontoApertura(e.target.value)}
                        placeholder="0"
                        className="w-full px-4 py-3 rounded-xl bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 text-slate-900 dark:text-white text-lg font-bold focus:outline-none focus:ring-2 focus:ring-brand-500/40"
                    />
                    <p className="text-xs text-slate-400 dark:text-slate-600 mt-1.5">Monto de billetes/monedas con que iniciás el turno.</p>
                </div>
                <button type="submit"
                    className="w-full py-3 rounded-xl bg-brand-600 hover:bg-brand-500 text-negro-950 font-bold text-sm transition-all shadow-lg shadow-brand-500/20 flex items-center justify-center gap-2">
                    <Play size={16} /> Abrir Turno
                </button>
            </form>
        </div>
    );

    const desglose = turno.desglose_pago || {};
    const duracion = Math.floor((Date.now() - new Date(turno.fecha_apertura)) / 60000);

    return (
        <div className="max-w-3xl mx-auto space-y-6 pb-10">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                <div>
                    <h2 className="titulo-pagina">Caja en Curso</h2>
                    <p className="text-sm text-slate-500 dark:text-slate-400 flex items-center gap-1.5 mt-0.5">
                        <Clock size={13} /> Abierta {fmtTime(turno.fecha_apertura)} · {duracion} min activo
                    </p>
                </div>
                <button onClick={fetchEstado}
                    className="p-2.5 rounded-xl border border-slate-200 dark:border-white/10 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-white/10 transition-all">
                    <RefreshCw size={16} />
                </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                <KpiCard label="Efectivo inicial" value={`Gs. ${fmt(turno.monto_apertura)}`} color="neutro" />
                <KpiCard label="Ventas del turno" value={`Gs. ${fmt(turno.total_ventas)}`} color="marca" />
                <KpiCard label="Efectivo esperado" value={`Gs. ${fmt(turno.efectivo_esperado)}`} color="marca" note="inicial + ventas efectivo" />
                <KpiCard label="Ventas (#)" value={turno.cantidad_ventas} color="marca" />
            </div>

            <div className="bg-white dark:bg-slate-900/60 border border-slate-200 dark:border-white/10 rounded-2xl p-5 shadow-sm">
                <h3 className="text-sm font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-4">Desglose por forma de pago</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {Object.entries(FORMA_META).map(([key, meta]) => {
                        const d = desglose[key] || { total: 0, cantidad: 0 };
                        return (
                            <div key={key} className={`flex items-center gap-3 p-4 rounded-xl border ${COLOR[meta.color]}`}>
                                <span className="opacity-70">{meta.icon}</span>
                                <div className="flex-1 min-w-0">
                                    <p className="text-xs font-bold uppercase tracking-wide opacity-70">{meta.label}</p>
                                    <p className="text-base font-bold">Gs. {fmt(d.total)}</p>
                                    <p className="text-xs opacity-60">{d.cantidad} venta{d.cantidad !== 1 ? 's' : ''}</p>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            <form onSubmit={handleCerrar} className="bg-white dark:bg-slate-900/60 border border-error-200 dark:border-error-500/20 rounded-2xl p-6 space-y-5 shadow-sm">
                <h3 className="font-bold text-slate-900 dark:text-white flex items-center gap-2">
                    <StopCircle size={18} className="text-error-500" /> Cerrar Caja
                </h3>
                <div>
                    <label htmlFor="caja-efectivo-contado-al-cierre-gs-2" className="etiqueta-campo">Efectivo contado al cierre (Gs.)</label>
                    <input id="caja-efectivo-contado-al-cierre-gs-2"
                        type="number" min="0" step="any"
                        value={montoDeclarado}
                        onChange={e => setMontoDeclarado(e.target.value)}
                        placeholder={`Esperado: Gs. ${fmt(turno.efectivo_esperado)}`}
                        className="w-full px-4 py-3 rounded-xl bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 text-slate-900 dark:text-white text-lg font-bold focus:outline-none focus:ring-2 focus:ring-error-500/40"
                    />
                    <p className="text-xs text-slate-400 mt-1">Contá el efectivo físico y escribilo aquí para detectar diferencias.</p>
                </div>
                <div>
                    <label htmlFor="caja-observaciones-2" className="etiqueta-campo">Observaciones</label>
                    <textarea id="caja-observaciones-2" rows={2} value={observaciones} onChange={e => setObservaciones(e.target.value)}
                        placeholder="Novedades del turno, diferencias, etc."
                        className="w-full px-4 py-3 rounded-xl bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 text-slate-900 dark:text-white text-sm resize-none focus:outline-none focus:ring-2 focus:ring-error-500/40"
                    />
                </div>
                <button type="submit" disabled={cerrando}
                    className="w-full py-3 rounded-xl bg-error-500 hover:bg-error-600 disabled:opacity-50 text-white font-bold text-sm transition-all flex items-center justify-center gap-2">
                    <StopCircle size={16} />
                    {cerrando ? 'Cerrando...' : 'Confirmar Cierre de Caja'}
                </button>
            </form>
        </div>
    );
}

/* ─────────────────────────────────────────────────────────
   Componentes compartidos
───────────────────────────────────────────────────────── */
function KpiCard({ label, value, color, note }) {
    const cls = {
        marca:    'bg-brand-50 border-brand-200 text-brand-800 dark:bg-brand-500/10 dark:border-brand-500/20 dark:text-brand-400',
        problema: 'bg-error-50 border-error-200 text-error-700 dark:bg-error-500/10 dark:border-error-500/20 dark:text-error-400',
        neutro:   'bg-slate-50 border-slate-200 text-slate-700 dark:bg-slate-800/60 dark:border-white/10 dark:text-slate-300',
    };
    return (
        <div className={`p-4 rounded-2xl border ${cls[color]}`}>
            <p className="text-xs font-bold uppercase tracking-wide opacity-60 mb-1">{label}</p>
            <p className="text-xl font-bold">{value}</p>
            {note && <p className="text-[10px] opacity-50 mt-0.5">{note}</p>}
        </div>
    );
}

function Reporte({ data }) {
    const diferencia = data.diferencia;
    const hayDif = diferencia !== null && diferencia !== undefined;
    const positivo = hayDif && diferencia >= 0;

    return (
        <div className="space-y-5 print:space-y-4">
            <div className="bg-white dark:bg-slate-900/60 border border-slate-200 dark:border-white/10 rounded-2xl p-5 print:border-black print:rounded-none">
                <div className="flex justify-between items-start flex-wrap gap-3">
                    <div>
                        <p className="text-xs text-slate-400 uppercase tracking-widest">Cajero</p>
                        <p className="font-bold text-slate-900 dark:text-white text-lg">{data.cajero_nombre}</p>
                        <p className="text-sm text-slate-500">{data.cajero_email}</p>
                    </div>
                    <div className="text-right">
                        <p className="text-xs text-slate-400 uppercase tracking-widest">Período</p>
                        <p className="text-sm font-semibold text-slate-700 dark:text-slate-300">{fmtTime(data.fecha_apertura)}</p>
                        <p className="text-sm font-semibold text-slate-700 dark:text-slate-300">→ {fmtTime(data.fecha_cierre)}</p>
                    </div>
                </div>
            </div>

            <div className="bg-white dark:bg-slate-900/60 border border-slate-200 dark:border-white/10 rounded-2xl p-5">
                <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-4">Cobros por Forma de Pago</h4>
                <div className="space-y-2">
                    {Object.entries(FORMA_META).map(([key, meta]) => {
                        const d = data.desglose_pago?.[key] || { total: 0, cantidad: 0 };
                        if (d.cantidad === 0) return null;
                        return (
                            <div key={key} className="flex items-center justify-between py-2 border-b border-slate-100 dark:border-white/5 last:border-0">
                                <span className={`flex items-center gap-2 text-sm font-semibold ${COLOR[meta.color].split(' ').filter(c => c.startsWith('text-') || c.startsWith('dark:text-')).join(' ')}`}>
                                    {meta.icon} {meta.label}
                                    <span className="font-normal text-slate-400">({d.cantidad} venta{d.cantidad !== 1 ? 's' : ''})</span>
                                </span>
                                <span className="font-bold text-slate-900 dark:text-white">Gs. {fmt(d.total)}</span>
                            </div>
                        );
                    })}
                    <div className="flex justify-between pt-2 font-bold text-base text-slate-900 dark:text-white">
                        <span>Total Ventas ({data.cantidad_ventas})</span>
                        <span>Gs. {fmt(data.total_ventas)}</span>
                    </div>
                </div>
            </div>

            {data.cantidad_ventas_periodo > data.cantidad_ventas && (
                <div className="bg-white dark:bg-slate-900/60 border border-slate-200 dark:border-white/10 rounded-2xl p-5">
                    <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Total del Período</h4>
                    <p className="text-xs text-slate-400 dark:text-slate-500 mb-4">Incluye ventas de todos los usuarios durante este turno</p>
                    <div className="space-y-2">
                        {Object.entries(FORMA_META).map(([key, meta]) => {
                            const d = data.desglose_pago_periodo?.[key] || { total: 0, cantidad: 0 };
                            if (d.cantidad === 0) return null;
                            return (
                                <div key={key} className="flex items-center justify-between py-2 border-b border-slate-100 dark:border-white/5 last:border-0">
                                    <span className={`flex items-center gap-2 text-sm font-semibold ${COLOR[meta.color].split(' ').filter(c => c.startsWith('text-') || c.startsWith('dark:text-')).join(' ')}`}>
                                        {meta.icon} {meta.label}
                                        <span className="font-normal text-slate-400">({d.cantidad} venta{d.cantidad !== 1 ? 's' : ''})</span>
                                    </span>
                                    <span className="font-bold text-slate-900 dark:text-white">Gs. {fmt(d.total)}</span>
                                </div>
                            );
                        })}
                        <div className="flex justify-between pt-2 font-bold text-base text-slate-900 dark:text-white">
                            <span>Total Período ({data.cantidad_ventas_periodo})</span>
                            <span>Gs. {fmt(data.total_periodo)}</span>
                        </div>
                    </div>
                </div>
            )}

            <div className="bg-white dark:bg-slate-900/60 border border-slate-200 dark:border-white/10 rounded-2xl p-5">
                <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-4">Cuadre de Efectivo</h4>
                <div className="space-y-3 text-sm">
                    <Row label="Efectivo inicial" value={`Gs. ${fmt(data.monto_apertura)}`} />
                    <Row label="Ventas cobradas en efectivo" value={`+ Gs. ${fmt(data.desglose_pago?.efectivo?.total || 0)}`} />
                    <Row label="Efectivo esperado en caja" value={`Gs. ${fmt(data.efectivo_esperado)}`} bold />
                    {data.monto_efectivo_declarado !== null && (
                        <Row label="Efectivo contado al cierre" value={`Gs. ${fmt(data.monto_efectivo_declarado)}`} />
                    )}
                    {hayDif && (
                        <div className={`flex justify-between items-center font-bold text-base px-4 py-3 rounded-xl border ${positivo ? 'bg-exito-50 border-exito-200 text-exito-700 dark:bg-exito-500/10 dark:border-exito-500/20 dark:text-exito-400' : 'bg-error-50 border-error-200 text-error-700 dark:bg-error-500/10 dark:border-error-500/20 dark:text-error-400'}`}>
                            <span className="flex items-center gap-2">
                                {positivo ? <CheckCircle2 size={16} /> : <AlertTriangle size={16} />}
                                {positivo ? 'Sobrante' : 'Faltante'}
                            </span>
                            <span>Gs. {fmt(Math.abs(diferencia))}</span>
                        </div>
                    )}
                </div>
            </div>

            {data.cantidad_anuladas > 0 && (
                <div className="bg-error-50 dark:bg-error-500/5 border border-error-200 dark:border-error-500/20 rounded-2xl p-4 text-sm text-error-600 dark:text-error-400 font-semibold">
                    ⚠ {data.cantidad_anuladas} venta{data.cantidad_anuladas !== 1 ? 's' : ''} anulada{data.cantidad_anuladas !== 1 ? 's' : ''} durante este turno.
                </div>
            )}

            {data.observaciones && (
                <div className="bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-2xl p-4">
                    <p className="text-xs font-bold text-slate-400 uppercase mb-1">Observaciones</p>
                    <p className="text-sm text-slate-700 dark:text-slate-300">{data.observaciones}</p>
                </div>
            )}

            <div className="bg-white dark:bg-slate-900/60 border border-slate-200 dark:border-white/10 rounded-2xl overflow-hidden">
                <div className="px-5 py-4 border-b border-slate-100 dark:border-white/10 flex items-center justify-between">
                    <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider">Ventas del Turno</h4>
                    <span className="text-xs text-slate-400">{data.ventas.length} venta{data.ventas.length !== 1 ? 's' : ''}</span>
                </div>
                {data.ventas.length === 0 && (
                    <p className="p-5 text-center text-slate-400 text-sm">Sin ventas en este turno.</p>
                )}
                {Object.entries(FORMA_META).map(([key, meta]) => {
                    const grupo = data.ventas.filter(v => v.forma_pago === key);
                    if (grupo.length === 0) return null;
                    const subtotal = grupo.reduce((a, v) => a + v.total, 0);
                    return (
                        <div key={key}>
                            <div className={`flex items-center justify-between px-5 py-2 text-xs font-bold border-b ${COLOR[meta.color]} opacity-90`}>
                                <span className="flex items-center gap-1.5">{meta.icon} {meta.label} ({grupo.length})</span>
                                <span>Gs. {fmt(subtotal)}</span>
                            </div>
                            <div className="divide-y divide-slate-100 dark:divide-white/5 print:max-h-none max-h-48 overflow-y-auto">
                                {grupo.map(v => (
                                    <div key={v.id} className="flex items-center justify-between px-5 py-2.5 text-sm">
                                        <span className="font-mono text-brand-800 w-10 shrink-0">#{v.id}</span>
                                        <span className="text-slate-500 dark:text-slate-400 flex-1 mx-3 truncate text-xs">{v.cliente}</span>
                                        <span className="text-xs text-slate-400 mr-3">{new Date(v.fecha).toLocaleTimeString('es-PY', { hour: '2-digit', minute: '2-digit' })}</span>
                                        <span className="font-bold text-slate-900 dark:text-white shrink-0">Gs. {fmt(v.total)}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function Row({ label, value, bold }) {
    return (
        <div className={`flex justify-between items-center ${bold ? 'font-bold text-slate-900 dark:text-white border-t border-slate-200 dark:border-white/10 pt-2 mt-1' : 'text-slate-600 dark:text-slate-400'}`}>
            <span>{label}</span>
            <span>{value}</span>
        </div>
    );
}
